import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, useMe } from 'src/common';

import styles from './index.module.scss';
import {
  useCreateFolder,
  useCurrentFolderContext,
  getUpdateCacheOnFolderCreate,
  useLoadDocuments
} from 'src/models/documentsLibrary/hooks';
import { FOLDER_NAME_PATTERN } from '../../types';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import { v4 } from 'uuid';
import dayjs from 'src/helpers/dayjs';
import Button from 'src/common/Button';

interface IFormState {
  name: string;
}

interface IProps {}

export const DocumentsCreateFolder: FC<IProps> = () => {
  const { t } = useTranslation();
  const currentFolder = useCurrentFolderContext();
  const [createFolder, { loading: createFolderLoading }] = useCreateFolder();
  const { data: currentDocs } = useLoadDocuments(currentFolder.id);

  const { data: me } = useMe();

  const [createFolderVisible, setCreateFolderVisible] = useState(false);

  const [form] = Form.useForm();

  const initialValues = useMemo((): IFormState => {
    return {
      name: ''
    };
  }, []);

  const onOpen = useCallback(() => {
    setCreateFolderVisible(true);
  }, []);
  const onClose = useCallback(() => {
    form.resetFields();
    setCreateFolderVisible(false);
  }, [form]);

  const folders = useMemo(() => {
    return currentDocs?.filter(doc => doc.nodeType === DocsNodeType.FOLDER);
  }, [currentDocs]);

  const onCreateFolder = useCallback(() => {
    const values = form.getFieldsValue();
    const { name: folderName } = values;
    const sameName = !!folders?.some(folder => folder.name === folderName);
    if (sameName) {
      form.setFields([
        {
          name: 'name',
          errors: [t('DocumentLibrary.existingFolderNameError')]
        }
      ]);
    } else {
      if (me) {
        const { email, name, lastName, color } = me;
        createFolder({
          variables: { name: folderName, parentId: currentFolder.id },
          update: getUpdateCacheOnFolderCreate(currentFolder.id),
          optimisticResponse: {
            __typename: 'Mutation',
            mkdir: {
              __typename: 'DirectoryNode',
              id: v4(),
              name: folderName,
              changedAt: t('Common.Format.utcDateTimeFormat', { date: dayjs().toDate() }),
              createDateTime: t('Common.Format.utcDateTimeFormat', { date: dayjs().toDate() }),
              createdBy: {
                email,
                name: name ?? '',
                lastName: lastName ?? '',
                color: color ?? null,
                enabled: true,
                __typename: 'User'
              },
              nodeType: DocsNodeType.FOLDER,
              children: []
            }
          }
        })
          .then(() => {
            onClose();
          })
          .catch(() => {
            form.setFields([
              {
                name: 'name',
                errors: [t('Common.unknownErrorDesc')]
              }
            ]);
          });
      }
    }
  }, [createFolder, currentFolder.id, folders, form, me, onClose, t]);

  const onKeyPress = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  return (
    <>
      <Button type={'default'} onClick={onOpen}>
        {t('DocumentLibrary.addFolder')}
      </Button>
      {createFolderVisible && (
        <Modal
          title={<span>{t('DocumentLibrary.createFolder')}</span>}
          visible
          onCancel={onClose}
          maskClosable
          width={500}
          onOk={onCreateFolder}
          footer={null}
          keyboard={false}
        >
          <Form
            initialValues={initialValues}
            onFinish={onCreateFolder}
            form={form}
            layout={'vertical'}
            className={styles.form}
            onKeyPress={e => onKeyPress(e)}
          >
            <Form.Item
              name={'name'}
              label={t('DocumentLibrary.folderName')}
              rules={[
                { required: true, message: t('DocumentLibrary.folderNameRequired') },
                { pattern: new RegExp(FOLDER_NAME_PATTERN), message: t('DocumentLibrary.folderNamePolicy') }
              ]}
            >
              <Input type={'text'} placeholder={t('DocumentLibrary.folderName')} onPressEnter={onCreateFolder} />
            </Form.Item>
            <div className={styles.formFooter}>
              <Button type={'text'} onClick={onClose} className={styles.button}>
                {t('Common.cancel')}
              </Button>
              <Button type={'primary'} htmlType={'submit'} className={styles.button} loading={createFolderLoading}>
                {t('Common.save')}
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default DocumentsCreateFolder;
